import { useEffect, useState, useCallback, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { getSurveysList, getSurveyTypes, Survey, SurveyType, DataTopicStatus } from "@/services/surveysListService"
import SurveyExpandedTable, { columns, columnGroups as expandedColumnGroups } from "@/components/survey-catalogue/SurveyExpandedTable"
import SurveyCompactTable, { columnGroups as compactColumnGroups } from "@/components/survey-catalogue/SurveyCompactTable"
import SurveyNotesPanel from "@/components/survey-catalogue/SurveyNotesPanel"
import { Link, useSearchParams } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import TableViewSelector, { ViewMode } from "@/components/shared/TableViewSelector"
import SearchButton from "@/components/shared/SearchButton"
import TableFiltersSelector, { FilterOption } from "@/components/shared/TableFiltersSelector"
import { Item, ItemContent, ItemTitle } from "@/components/ui/item.tsx"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Button } from "@/components/ui/button"
import { Plus } from "lucide-react"

const allColumnKeys = new Set([...columns.map((c) => c.key), "dataTopics" as keyof Survey])

const fixedColumns = new Set<keyof Survey>(["mapSurveyId", "countryName", "year", "surveyType", "surveyStatus"])

const badgeColumns = new Set<keyof Survey>([
  "feverTreatmentSeeking",
  "antimalarials",
  "chemoprevention",
  "itn",
  "irs",
  "improvedHousing",
  "rdt",
  "microscopy",
])

function parseVisibleKeys(param: string | null): Set<keyof Survey> {
  if (!param) return new Set(allColumnKeys)
  const keys = param.split(",").filter((k) => allColumnKeys.has(k as keyof Survey))
  return keys.length > 0 ? new Set(keys as (keyof Survey)[]) : new Set(allColumnKeys)
}

function hasSurveyData(survey: Survey): boolean {
  if (survey.hasMicro) {
    return true
  }
  const extractedValues = new Set<string>([
    DataTopicStatus.MICRO_DATA_EXTRACTED,
    DataTopicStatus.REPORT_DATA_EXTRACTED,
    DataTopicStatus.DOES_CONTAIN,
  ])
  return [...badgeColumns].some((key) => extractedValues.has(String(survey[key])))
}

function getTableFilterOptions(surveys: Survey[]): FilterOption[] {
  return [
    {
      key: "countryName",
      label: "Country",
      options: [...new Set(surveys.map(survey => survey.countryName))].sort(),
    },
    {
      key: "year",
      label: "Year",
      options: [...new Set(surveys.map(survey => survey.year))].sort(),
    },
    {
      key: "surveyType",
      label: "Survey Type",
      options: [...new Set(surveys.map(survey => survey.surveyType))].sort(),
    },
    {
      key: "populationRepresentativeness",
      label: "Targeted Population",
      // populationRepresentativeness is optional on Survey, so some surveys have
      // no value. Filter those out so only real values become selectable options.
      options: [
        ...new Set(
          surveys
            .map(survey => survey.populationRepresentativeness)
            .filter((value): value is NonNullable<Survey["populationRepresentativeness"]> => value != null)
        ),
      ].sort(),
    },
    {
      key: "surveyStatus",
      label: "Survey Status",
      options: [...new Set(surveys.map(survey => survey.surveyStatus))].sort(),
    },
  ]
}

const PAGE_TITLE = "Survey Catalogue"

type DataFilter = "all" | "with-data" | "without-data"

const DATA_FILTER_OPTIONS: { value: DataFilter; label: string; countClass?: string }[] = [
  { value: "all", label: "All" },
  { value: "with-data", label: "With Data", countClass: "text-chart-3" },
  { value: "without-data", label: "No Data", countClass: "text-muted-foreground" },
]

const SurveyInterface = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { getAccessToken } = useAuth()

  const [surveys, setSurveys] = useState<Survey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Survey type code → description mapping loaded from the API
  const [surveyTypeLabels, setSurveyTypeLabels] = useState<Record<string, string>>({})

  // Initialize state from URL params
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const param = searchParams.get("view")
    const valid: ViewMode[] = ["compact", "expanded", "full"]
    return param && valid.includes(param as ViewMode) ? (param as ViewMode) : "expanded"
  })
  const [visibleKeys, setVisibleKeys] = useState<Set<keyof Survey>>(
    () => parseVisibleKeys(searchParams.get("columns"))
  )
  const [dataFilter, setDataFilter] = useState<DataFilter>(
    () => (searchParams.get("dataFilter") as DataFilter) || "all"
  )
  const [searchQuery, setSearchQuery] = useState<string>(
    () => searchParams.get("search") || ""
  )
  const [activeFilters, setActiveFilters] = useState<Map<string, Set<string>>>(() => {
    const filters = new Map<string, Set<string>>()
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter.")) {
        const filterKey = key.slice(7)
        filters.set(filterKey, new Set(value.split(",")))
      }
    })
    return filters
  })

  /** The survey currently selected for the notes side panel (null = closed). */
  const [notesPanelSurveyId, setNotesPanelSurveyId] = useState<string | null>(null)

  /** Derive the notes panel survey from the current surveys list so it stays in sync. */
  const notesPanelSurvey = useMemo<Survey | null>(() => {
    if (notesPanelSurveyId === null) return null
    return surveys.find((s) => s.mapSurveyId === notesPanelSurveyId) ?? null
  }, [surveys, notesPanelSurveyId])

  // Sync state changes back to URL
  useEffect(() => {
    const params: Record<string, string> = {}

    if (viewMode !== "expanded") {
      params.view = viewMode
    }
    if (dataFilter !== "all") {
      params.dataFilter = dataFilter
    }
    if (searchQuery) {
      params.search = searchQuery
    }
    const currentColumnGroups = viewMode === "compact" ? compactColumnGroups : expandedColumnGroups
    const currentDefaultKeys = currentColumnGroups.flatMap((g) => g.keys)
    const allCurrentVisible = currentDefaultKeys.every((k) => visibleKeys.has(k))
    if (!allCurrentVisible) {
      // Only include keys relevant to the current view
      const relevantVisible = [...visibleKeys].filter((k) => currentDefaultKeys.includes(k))
      params.columns = relevantVisible.join(",")
    }
    activeFilters.forEach((values, key) => {
      if (values.size > 0) {
        params[`filter.${key}`] = [...values].join(",")
      }
    })

    setSearchParams(params, { replace: true })
  }, [
    viewMode,
    dataFilter,
    searchQuery,
    visibleKeys,
    activeFilters,
    setSearchParams,
  ])

  const fetchSurveys = useCallback(async (): Promise<void> => {
    try {
      const token = await getAccessToken()
      const [data, types] = await Promise.all([
        getSurveysList(token),
        getSurveyTypes(token),
      ])
      setSurveys(data)
      // Build a code → description lookup map from the survey types table
      const labels: Record<string, string> = {}
      types.forEach((t: SurveyType) => {
        if (t.description) {
          labels[t.surveyType] = t.description
        }
      })
      setSurveyTypeLabels(labels)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load surveys")
    } finally {
      setLoading(false)
    }
  }, [getAccessToken])

  useEffect(() => {
    fetchSurveys()
  }, [fetchSurveys])

  // Apply sequential filters to calculate counts & subset
  const { filteredSurveys, totalCount, withDataCount, withoutDataCount } = useMemo(() => {
    let list = surveys

    // 1. Apply filters from TableFiltersSelector
    activeFilters.forEach((selectedValues, key) => {
      if (selectedValues.size > 0) {
        list = list.filter((s) => selectedValues.has(String(s[key as keyof Survey])))
      }
    })

    // 2. Search input (searches country, map survey id, year, survey type, and status)
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase()
      list = list.filter((s) =>
        (s.countryName && s.countryName.toLowerCase().includes(query)) ||
        (s.mapSurveyId && s.mapSurveyId.toLowerCase().includes(query)) ||
        (s.year != null && s.year.includes(query)) ||
        (s.surveyType && s.surveyType.toLowerCase().includes(query)) ||
        (s.surveyStatus && s.surveyStatus.toLowerCase().includes(query))
      )
    }

    // 3. Calculate availability counts before applying the dataFilter itself
    const total = list.length
    const withData = list.filter(hasSurveyData).length
    const withoutData = total - withData

    // 4. Apply Quick Card Filter (All / With Data / Without Data)
    if (dataFilter === "with-data") {
      list = list.filter((s) => hasSurveyData(s))
    } else if (dataFilter === "without-data") {
      list = list.filter((s) => !hasSurveyData(s))
    }

    return {
      filteredSurveys: list,
      totalCount: total,
      withDataCount: withData,
      withoutDataCount: withoutData,
    }
  }, [surveys, activeFilters, searchQuery, dataFilter])

  if (loading) {
    return <LoadingSpinner loadingContent="Loading surveys..." />
  }

  if (error) {
  return (
    <div className=" flex flex-col justify-center h-full">
      <div className="flex align-middle justify-center w-full">
        <Item variant="outline" className="w-auto">
          <ItemContent>
            <ItemTitle className="text-destructive">Error: {error}</ItemTitle>
          </ItemContent>
        </Item>
      </div>
    </div> )
  }

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <Card className="flex flex-1 flex-col min-h-0 shadow-none gap-4">
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>
                <h1>{PAGE_TITLE}</h1>
              </CardTitle>
              <CardDescription>Explore the MAP survey catalogue</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/survey-catalogue/new">
                <Plus data-icon="inline-start" />
                New Survey
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0">
          {/* Survey Toolbar */}
          <div className="flex flex-col gap-4 mb-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-4 mr-auto">
                  {DATA_FILTER_OPTIONS.map(({ value, label, countClass }) => {
                    const counts: Record<DataFilter, number> = {
                      all: totalCount,
                      "with-data": withDataCount,
                      "without-data": withoutDataCount,
                    }
                    return (
                      <button
                        key={value}
                        onClick={() => setDataFilter(value)}
                        className={`text-sm/3.5 h-8 inline-flex items-center gap-1.5 px-2.5 py-1.5 font-semibold border transition-all ${
                          dataFilter === value
                            ? "border-primary ring-2 ring-primary/20"
                            : "border-border hover:bg-accent/40"
                        }`}
                      >
                        <span className="text-muted-foreground">{label}</span>
                        <span className={`font-extrabold text-xs ${countClass ?? ""}`}>
                          {counts[value]}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-4">
                  <SearchButton value={searchQuery} onChange={setSearchQuery} />
                  <TableFiltersSelector
                    filterOptions={getTableFilterOptions(surveys)}
                    onSave={setActiveFilters}
                    initialFilters={activeFilters}
                  />
                  <TableViewSelector
                    value={viewMode}
                    onChange={setViewMode}
                    options={["compact", "expanded"]}
                    columnGroups={
                      viewMode === "compact" ? compactColumnGroups : expandedColumnGroups
                    }
                    visibleKeys={visibleKeys}
                    fixedColumns={fixedColumns}
                    onSaveColumns={setVisibleKeys}
                  />
                </div>
              </div>
          </div>
          {/* Table views + notes panel */}
          <div className="flex flex-1 min-h-0">
            <div className="flex-1 min-w-0 flex flex-col">
              {viewMode === "compact" && (
                <SurveyCompactTable
                  surveys={filteredSurveys}
                  visibleKeys={visibleKeys}
                  onSurveyUpdated={fetchSurveys}
                  onViewNotes={survey => setNotesPanelSurveyId(survey.mapSurveyId)}
                  onAddReportFile={fetchSurveys}
                  surveyTypeLabels={surveyTypeLabels}
                />
              )}
              {viewMode === "expanded" && (
                <SurveyExpandedTable
                  surveys={filteredSurveys}
                  visibleKeys={visibleKeys}
                  onSurveyUpdated={fetchSurveys}
                  onViewNotes={survey => setNotesPanelSurveyId(survey.mapSurveyId)}
                  onAddReportFile={fetchSurveys}
                  surveyTypeLabels={surveyTypeLabels}
                />
              )}
            </div>
            {notesPanelSurvey && (
              <SurveyNotesPanel
                survey={notesPanelSurvey}
                onClose={() => setNotesPanelSurveyId(null)}
                onNoteAdded={fetchSurveys}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export default SurveyInterface
